import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, UserPlus, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";

export default function TeamTab() {
  const { isRTL } = useI18n();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const { data: team = [], isLoading } = useQuery({
    queryKey: ["mycafe-team"], queryFn: endpoints.myCafeTeam,
  });
  const { data: sharedWithMe = [] } = useQuery({
    queryKey: ["mycafe-shared"], queryFn: endpoints.myCafeSharedWithMe,
  });

  const add = useMutation({
    mutationFn: () => endpoints.myCafeAddTeamMember({ employee_email: email, employee_name: name || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mycafe-team"] });
      toast.success(isRTL ? "تمت إضافة الموظف" : "Employee added");
      setEmail(""); setName("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  const remove = useMutation({
    mutationFn: (id) => endpoints.myCafeRemoveTeamMember(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mycafe-team"] }),
  });

  return (
    <div className="space-y-8" data-testid="mycafe-team">
      <div>
        <p className="text-sm text-[#5C544F] mb-4">
          {isRTL
            ? "أضف موظفيك بإيميلاتهم. عندما يسجّلون الدخول لـ \"مقهاي\" بنفس الإيميل، تظهر لهم وصفاتك (إسبريسو · قهوة اليوم · مشروبات مخصصة) بصلاحية قراءة فقط."
            : "Add employees by email. When they log in to My Cafe with the same email, they see your recipes (espresso · drip · custom drinks) in read-only mode."}
        </p>
        <div className="soft-card rounded-2xl p-5">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label>{isRTL ? "إيميل الموظف" : "Employee email"} *</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="staff@example.com" className="mt-1.5 h-11" data-testid="team-email-input" />
            </div>
            <div>
              <Label>{isRTL ? "اسم الموظف (اختياري)" : "Employee name (optional)"}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" data-testid="team-name-input" />
            </div>
            <div className="flex items-end">
              <Button onClick={() => add.mutate()} disabled={add.isPending || !email.trim()} className="bg-[#C86240] text-white h-11 w-full" data-testid="team-add-btn">
                <UserPlus className="w-4 h-4 me-1.5" />
                {isRTL ? "إضافة" : "Add"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-[0.25em] text-[#C86240] mb-3">{isRTL ? "موظفوك" : "Your team"}</h3>
        {isLoading ? (
          <div className="text-[#5C544F] text-sm">{isRTL ? "جارٍ التحميل..." : "Loading..."}</div>
        ) : team.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#2A2421]/15 p-8 text-center">
            <Users className="w-8 h-8 mx-auto text-[#C86240] mb-2" />
            <div className="text-sm text-[#5C544F]">{isRTL ? "لم تتم إضافة موظفين بعد" : "No employees added yet"}</div>
          </div>
        ) : (
          <div className="space-y-2">
            {team.map((m) => (
              <div key={m.id} className="soft-card rounded-xl p-4 flex items-center justify-between" data-testid={`team-row-${m.id}`}>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-[#5C544F]" />
                  <div>
                    <div className="text-sm text-[#2A2421]">{m.employee_name || m.employee_email}</div>
                    {m.employee_name && <div className="text-xs text-[#5C544F]">{m.employee_email}</div>}
                  </div>
                </div>
                <button
                  onClick={() => window.confirm(isRTL ? "إزالة الموظف؟" : "Remove employee?") && remove.mutate(m.id)}
                  className="p-2 hover:bg-red-50 rounded-lg"
                  data-testid={`team-del-${m.id}`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {sharedWithMe.length > 0 && (
        <div className="border-t border-[#2A2421]/10 pt-6">
          <h3 className="text-xs uppercase tracking-[0.25em] text-[#3D6F86] mb-3">{isRTL ? "وصفات مشاركة معي" : "Recipes shared with me"}</h3>
          {sharedWithMe.map((s) => (
            <div key={s.owner_id} className="soft-card rounded-2xl p-5 mb-3" data-testid={`shared-${s.owner_id}`}>
              <div className="text-[#2A2421] font-medium">{s.owner_name || s.owner_email}</div>
              <div className="text-xs text-[#5C544F] mt-1">
                {isRTL ? `إسبريسو: ${s.espresso.length} · قهوة اليوم: ${s.drip.length} · مخصصة: ${s.custom.length}` : `${s.espresso.length} espresso · ${s.drip.length} drip · ${s.custom.length} custom`}
              </div>
              <p className="text-xs text-[#5C544F] mt-2 italic">
                {isRTL ? "(عرض كامل قريباً — قيد البناء)" : "(full view coming soon — under construction)"}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
